import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { MedicationInfo } from 'components/Common/Medication/types'
import { BaseSubsectionAdapter } from '../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { DischargeFormData } from '../../types/formData'
import { DischargeFormField } from '../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { createAnswerItemsFromValues, getAnswerValuesFromItems } from '../../../helpers'
import { formatAssessmentMedications } from '../../../hooks/helpers'
import { AllQuestionnaireItemType } from '../../../hooks/constants'

/**
 * Medications subsection adapter
 */
export class MedicationsAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Medications summary field
    const medicationSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_SUMMARY
    )?.at(0)

    const medications: MedicationInfo[] = formatAssessmentMedications(flatAnswers)

    return {
      [DischargeFormField.MEDICATIONS_MEDICATION_SUMMARY]: medicationSummary,
      [DischargeFormField.MEDICATIONS_MEDICATION_LIST]: medications
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.MEDICATIONS_MEDICATION_SUMMARY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_SUMMARY,
        value: this.getFormDataField(DischargeFormField.MEDICATIONS_MEDICATION_SUMMARY)
      })
    }

    // Handle medications list using existing pattern
    const formMedications: MedicationInfo[] | undefined = this.formDataFieldExists(
      DischargeFormField.MEDICATIONS_MEDICATION_LIST
    )
      ? this.getFormDataField(DischargeFormField.MEDICATIONS_MEDICATION_LIST)
      : undefined
    if (formMedications && Array.isArray(formMedications)) {
      // Reverse the order to match most recent first
      const medicationAnswers = [...formMedications].reverse().map((medication) => {
        const questionnaireItemsAndValues: [AllQuestionnaireItemType, unknown][] = [
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DRUGBANK_PCID,
            medication.productConceptId
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_NAME,
            medication.name
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_ROUTE,
            medication.route
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_NUMBER,
            medication.strength?.number
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_UNIT,
            medication.strength?.unit
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INDICATIONS,
            medication.indications
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_FREQUENCY,
            medication.frequency
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_NUMBER,
            medication.dosage?.number
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_UNIT,
            medication.dosage?.unit
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INSTRUCTIONS,
            medication.instructions
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_USAGE_STATUS,
            medication.usageStatus
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_START_DATE,
            medication.startDate
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DISCONTINUE_DATE,
            medication.discontinueDate
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_CONTROLLED,
            medication.controlled
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_LIQUID,
            medication.liquid
          ],
          [
            DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INFECTION,
            medication.infection
          ]
        ]

        return {
          item: createAnswerItemsFromValues(questionnaireItemsAndValues)
        }
      })

      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO,
        value: medicationAnswers
      })
    }

    return mutations
  }
}
